import type { CriterionKey, Topic } from "./types";
import { PREPOSITIONS } from "./prepositions";
import { WORD_ORDER } from "./word-order";
import { CONNECTORS } from "./connectors";

/** Registry of all textbook topics. Order here = order on the index page. */
export const TOPICS: readonly Topic[] = [PREPOSITIONS, WORD_ORDER, CONNECTORS] as const;

export function getTopicBySlug(slug: string): Topic | undefined {
  return TOPICS.find((t) => t.slug === slug);
}

export function listTopics(): readonly Topic[] {
  return TOPICS;
}

/**
 * Score each topic against a (deel, criterion) signal and return the best
 * match — or null when no topic is meaningfully relevant.
 */
export function pickTopicFor(
  deel: number | null | undefined,
  criterion: CriterionKey | null | undefined,
): Topic | null {
  if (deel == null && criterion == null) return null;
  let best: Topic | null = null;
  let bestScore = 0;
  for (const t of TOPICS) {
    let s = 0;
    if (deel != null && t.relevantDeelen.includes(deel)) s += 2;
    if (criterion != null && t.relevantCriteria.includes(criterion)) s += 3;
    if (s > bestScore) {
      bestScore = s;
      best = t;
    }
  }
  return best;
}

/**
 * Pick up to N topics ranked by relevance to a set of weak deelen +
 * weak criteria. Used by /api/exam/report to suggest reading material
 * AFTER the session, where the student is no longer mid-exam.
 */
export function pickTopicsForReport(
  weakDeelen: readonly number[],
  weakCriteria: readonly CriterionKey[],
  max = 3,
): Topic[] {
  const scored = TOPICS.map((t) => {
    let score = 0;
    for (const d of weakDeelen) if (t.relevantDeelen.includes(d)) score += 2;
    for (const c of weakCriteria) if (t.relevantCriteria.includes(c)) score += 3;
    return { topic: t, score };
  })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, max)
    .map((x) => x.topic);
  return scored;
}
